import { useForm } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Loader2 } from "lucide-react";
import { PlacesAutocomplete } from "@/components/maps/PlacesAutocomplete";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { useState } from "react";

interface CompanyFormProps {
  initialData?: {
    id?: number;
    name: string;
    description?: string;
    location?: string;
    latitude?: number;
    longitude?: number;
  };
  mode: "create" | "edit";
}

interface CompanyFormValues {
  name: string;
  description: string;
  location?: string;
  latitude?: number;
  longitude?: number;
}

export function CompanyForm({ initialData, mode }: CompanyFormProps) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const form = useForm<CompanyFormValues>({
    defaultValues: {
      name: initialData?.name || "",
      description: initialData?.description || "",
      location: initialData?.location || "",
      latitude: initialData?.latitude,
      longitude: initialData?.longitude,
    },
  });

  const onSubmit = async (data: CompanyFormValues) => {
    if (isSubmitting) return; // Prevent multiple submissions
    
    try {
      setIsSubmitting(true);
      if (mode === "create") {
        // First get the current user's ID
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          toast({
            title: "Error",
            description: "You must be logged in to create a company",
            variant: "destructive",
          });
          return;
        }

        const { error } = await supabase
          .from("companies")
          .insert([{ 
            ...data, 
            user_id: user.id,
            location: data.location || null,
            latitude: data.latitude || null,
            longitude: data.longitude || null,
          }]);

        if (error) throw error;
        toast({ title: "Company created successfully" });
      } else {
        const { error } = await supabase
          .from("companies")
          .update({
            ...data,
            location: data.location || null,
            latitude: data.latitude || null,
            longitude: data.longitude || null,
          })
          .eq("id", initialData?.id);
        if (error) throw error;
        toast({ title: "Company updated successfully" });
      }
      navigate("/companies");
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePlaceSelect = (place: google.maps.places.PlaceResult & {
    latitude?: number;
    longitude?: number;
  }) => {
    if (place.formatted_address) {
      form.setValue("location", place.formatted_address);
    }
    if (place.latitude) {
      form.setValue("latitude", place.latitude);
    }
    if (place.longitude) {
      form.setValue("longitude", place.longitude);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Company Name</FormLabel>
              <FormControl>
                <Input placeholder="Enter company name" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Description</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Enter company description"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="location"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Location</FormLabel>
              <FormControl>
                <PlacesAutocomplete
                  value={field.value}
                  onChange={field.onChange}
                  onPlaceSelect={handlePlaceSelect}
                  placeholder="Enter company location"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex justify-end gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/companies")}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button 
            type="submit"
            disabled={isSubmitting}
            className="bg-blue-600 hover:bg-blue-700 text-white min-w-[100px]"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {mode === "create" ? "Creating..." : "Updating..."}
              </>
            ) : (
              mode === "create" ? "Create Company" : "Update Company"
            )}
          </Button>
        </div>
      </form>
    </Form>
  );
}